import type { FC, ReactNode, MouseEvent as ReactMouseEvent } from 'react';
import classnames from 'classnames';

import type { ButtonVariation } from 'app/components/Button';
import Button from 'app/components/Button';
import Modal from 'app/components/Modal/index';
import Tooltip from 'app/components/Tooltip';

import style from './style.scss';

interface Props {
  children: ReactNode;
  displayed: boolean;
  title?: ReactNode;
  variation?: ButtonVariation;
  onCancel: () => void;
  onConfirm?: (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onConfirmSecondary?: (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void;
  cancelText?: string;
  confirmText?: string | ReactNode;
  secondaryConfirmText?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  initialFocus?: number;
  disabledReason?: string;
  confirmTooltipText?: string;
  contentClassName?: string;
  onBack?: () => void;
  backButtonText?: string;
}

const ConfirmModal: FC<Props> = ({
  children,
  displayed,
  title,
  variation = 'primary',
  onCancel,
  onConfirm,
  onConfirmSecondary,
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  secondaryConfirmText,
  loading = false,
  disabled = false,
  className,
  confirmTooltipText,
  initialFocus,
  disabledReason,
  contentClassName,
  onBack,
  backButtonText
}: Props) => (
  <Modal
    displayed={displayed}
    className={className}
    onClickOutside={onCancel}
    initialFocus={initialFocus}
  >
    {title && <div className={style.title}>{title}</div>}
    <div className={classnames(style.content, contentClassName)}>{children}</div>
    <div className={classnames(style.buttons)}>
      <div className={style.buttonsContainer}>
        {onBack && backButtonText && (
          <Button variation="empty" onClick={onBack}>
            {backButtonText}
          </Button>
        )}
      </div>
      <div className={style.buttonsContainer}>
        <Button variation="cancel" onClick={onCancel}>
          {cancelText}
        </Button>
        {onConfirm && (
          <Tooltip
            active={!!confirmTooltipText}
            content={confirmTooltipText}
            keepReferenceWhenInactive
          >
            <Tooltip content={disabledReason} active={disabled && !!disabledReason}>
              <Button
                variation={variation}
                loading={loading}
                disabled={disabled}
                onClick={onConfirm}
              >
                {confirmText}
              </Button>
            </Tooltip>
          </Tooltip>
        )}
        {onConfirmSecondary && secondaryConfirmText && (
          <Button
            variation="empty_border"
            loading={loading}
            disabled={disabled}
            onClick={onConfirmSecondary}
          >
            {secondaryConfirmText}
          </Button>
        )}
      </div>
    </div>
  </Modal>
);

ConfirmModal.displayName = 'ConfirmModal';

export default ConfirmModal;
